import { useState, useEffect } from 'react';
import { mfaAPI } from '../../../services/api';

export const useMFASetup = () => {
  const [mfaState, setMfaState] = useState<{
    enabled: boolean;
    loading: boolean;
    step: 'status' | 'setup' | 'verify';
    setupData: { secret: string; qr_code: string } | null;
  }>({ enabled: false, loading: true, step: 'status', setupData: null });

  const { enabled: mfaEnabled, loading, step, setupData } = mfaState;

  const [codes, setCodes] = useState({ verifyCode: '', disableCode: '' });
  const { verifyCode, disableCode } = codes;

  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const { error, success } = feedback;

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await mfaAPI.getStatus();
      setMfaState(prev => ({ ...prev, enabled: res.data.mfa_enabled }));
    } catch {
      // ignore
    } finally {
      setMfaState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSetup = async () => {
    setFeedback({ error: '', success: '' });
    try {
      const res = await mfaAPI.setup();
      setMfaState(prev => ({ ...prev, setupData: { secret: res.data.secret, qr_code: res.data.qr_code }, step: 'verify' }));
    } catch (err: any) {
      setFeedback(prev => ({ ...prev, error: err?.response?.data?.error || 'Failed to start MFA setup.' }));
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(prev => ({ ...prev, error: '' }));
    try {
      await mfaAPI.enable(verifyCode);
      setMfaState(prev => ({ ...prev, enabled: true, step: 'status', setupData: null }));
      setFeedback({ error: '', success: 'MFA has been enabled successfully! Your account is now more secure.' });
      setCodes(prev => ({ ...prev, verifyCode: '' }));
    } catch (err: any) {
      setFeedback(prev => ({ ...prev, error: err?.response?.data?.error || 'Invalid verification code.' }));
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(prev => ({ ...prev, error: '' }));
    try {
      await mfaAPI.disable(disableCode);
      setMfaState(prev => ({ ...prev, enabled: false }));
      setFeedback({ error: '', success: 'MFA has been disabled.' });
      setCodes(prev => ({ ...prev, disableCode: '' }));
    } catch (err: any) {
      setFeedback(prev => ({ ...prev, error: err?.response?.data?.error || 'Invalid MFA code.' }));
    }
  };

  const handleCancelSetup = () => {
    setMfaState(prev => ({ ...prev, step: 'status', setupData: null }));
    setCodes(prev => ({ ...prev, verifyCode: '' }));
    setFeedback(prev => ({ ...prev, error: '' }));
  };

  const handleVerifyCodeChange = (value: string) => {
    setCodes(prev => ({ ...prev, verifyCode: value.replace(/\D/g, '').slice(0, 6) }));
  };

  const handleDisableCodeChange = (value: string) => {
    setCodes(prev => ({ ...prev, disableCode: value.replace(/\D/g, '').slice(0, 6) }));
  };

  return {
    mfaEnabled,
    loading,
    step,
    setupData,
    verifyCode,
    disableCode,
    error,
    success,
    handleSetup,
    handleEnable,
    handleDisable,
    handleCancelSetup,
    handleVerifyCodeChange,
    handleDisableCodeChange,
  };
};
